<template>
    <App title="Assign Financier Allocation">

        <div class="container">

            <!-- Page Header -->
            <div class="page-header mb-2">
                <a :href="route('financier.page.group')">
                    <div class="d-flex align-items-center gap-2 back-link mb-3">
                        <i class="fa fa-arrow-left fa-xs"></i>
                        <span>Back to financier</span>
                    </div>
                </a>

                <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <h1 class="page-title">Assign Financier Allocation</h1>

                    <div class="tab-toggle">
                        <button class="toggle-btn" :class="{ 'bg-secondary text-light': activeTab === 'single' }" @click="activeTab = 'single'">
                            <i class="fa fa-pen fa-xs me-2"></i>Single Assignment
                        </button>

                        <button class="toggle-btn" :class="{ 'bg-secondary text-light': activeTab === 'bulk' }" @click="activeTab = 'bulk'">
                            <i class="fa fa-layer-group fa-xs me-2"></i>Bulk Import
                        </button>
                    </div>
                </div>
            </div>

            <!-- Single -->
            <div v-show="activeTab === 'single'" class="panel-card rounded-0 border">

                <div class="panel-section-label">
                    <span class="section-dot"></span>
                    Allocation Information
                </div>

                <Form ref="form" @submit="onSubmit">

                    <div class="row g-4">

                        <div class="col-md-6">
                            <label class="field-label">Location Type</label>

                            <SelectField id="type" v-model="type" name="type" rules="required" label="Station Type" :options="type_options">
                                <option v-for="t in types" :key="t.value" :value="t.value">
                                    {{ t.value }}
                                </option>
                            </SelectField>
                        </div>

                        <div class="col-md-6">
                            <label class="field-label">
                                Location
                                <span v-if="type" class="selection-count ms-2">
                                    {{ type }}
                                </span>
                            </label>

                            <SelectField id="location" v-model="location" name="location" rules="required" label="Select Location" :options="location_options" />
                        </div>

                        <div class="col-12">
                            <label class="field-label">
                                Select Assets
                                <span v-if="asset.length" class="selection-count ms-2">
                                    {{ asset.length }} selected
                                </span>
                            </label>

                            <MultiSelectField id="asset" ref="assetField" v-model="asset" name="asset" rules="required" label="Select Engine" :options="assets_options" :key="assetEndpoint" />
                        </div>

                        <div v-if="selectedEngines.length" class="col-12">
                            <div class="preview-label mb-2">Selected engines</div>

                            <div class="d-flex flex-wrap gap-2">
                                <div v-for="engine in selectedEngines" :key="engine.id" class="engine-pill rounded-pill small text-muted">

                                    <i class="fa fa-cog fa-xs me-1 text-secondary bg-light"></i>
                                    {{ engine.title }}

                                    <button type="button" class="pill-remove btn  btn-xs" @click="removeEngine(engine.id)">
                                        <i class="fa fa-times fa-xs"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="col-12">
                            <div class="divider"></div>
                        </div>

                        <div class="col-12 d-flex justify-content-end gap-2">
                            <button type="button" class="btn bg-danger text-light" @click="goBack">
                                Cancel
                            </button>

                            <Button id="action" type="submit" :label="labels" :loading="loading" :done="done" class="btn bg-secondary text-light" :disabled="checkInput" />
                        </div>

                    </div>

                </Form>
            </div>

            <!-- Bulk -->
            <div v-show="activeTab === 'bulk'" class="panel-card rounded-0">

                <div class="panel-section-label">
                    <span class="section-dot"></span>
                    Bulk Import
                </div>

                <Dropzone id="dropzone" name="file" label="Upload Data Sheet file" :url="route('financier.ajax.import', { financier: financier.slug })" param="file" class="modern-dropzone" @success="onImportSuccess" @error="onImportError" />

                <div class="import-hint mt-3">
                    <i class="fa fa-circle-info fa-xs me-2 text-muted"></i>
                    Upload a <strong>.xlsx</strong> or <strong>.csv</strong> file.
                </div>

            </div>

        </div>

        <!-- Notifications -->
        <div class="position-relative">
            <div class="position-absolute top-0 end-0 mt-5">
                <Notivue v-slot="item">
                    <Notification :item="item" :theme="materialTheme">
                        <NotificationProgress :item="item" />
                    </Notification>
                </Notivue>
            </div>
        </div>

    </App>
</template>

<script lang="ts">
import $ from 'jquery'
import { materialTheme, Notification, NotificationProgress, Notivue } from 'notivue'
import Swal from 'sweetalert2'
import { Component, Prop, Ref, toNative, Vue, Watch } from 'vue-facing-decorator'

import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import MultiSelectField from '@/vue/components/MultiSelectField.vue'
import PhoneField from '@/vue/components/PhoneField.vue' // ✅ FIXED
import SelectField from '@/vue/components/SelectField.vue'
import App from '@/vue/templates/App.vue'
import Dropzone from '@/widget/Dropzone.vue'

import { useConfirm } from '@/libs/useConfirm.ts'
import { ButtonLabel } from '@/vue/utils/Button'
import type { Options as Select2Options } from 'select2'

@Component({
    components: {
        App,
        Form,
        Alert,
        InputField,
        PhoneField,
        Button,
        Block,
        SelectField,
        MultiSelectField,
        Dropzone,
        Notivue,
        Notification,
        NotificationProgress,
    },
})
class Create extends Vue {

    message = ''
    loading = false
    done = false

    activeTab: 'single' | 'bulk' = 'single'

    type = ''
    location = ''

    endpoint = ''
    assetEndpoint = ''

    asset: string[] = []
    engineCache: Record<string, any> = {}

    materialTheme = materialTheme
    confirm = useConfirm().confirm

    @Prop({ type: Object })
    readonly financier!: Record<string, any>

    @Ref('form') readonly form!: InstanceType<typeof Form>

    @Ref('assetField') assetField!: any

    get types() {
        return [
            { name: 'Warehouse', value: 'Warehouse' },
            { name: 'Station', value: 'Station' },
        ]
    }

    get type_options(): Select2Options {
        return {
            placeholder: 'Select Location Type',
            minimumResultsForSearch: Infinity,
            ajax: null,
        }
    }

    get location_options(): Select2Options {
        return {
            placeholder: 'Select Location',
            minimumResultsForSearch: 3,
            ajax: {
                method: 'get',
                url: this.endpoint,
            },
            templateResult: ({ id, title, text }: any) =>
                id ? $(`<span>${title}</span>`) : text,

            templateSelection: (item: any) => {
                if (item.id) this.engineCache[item.id] = item
                return item.id ? $(`<span>${item.title}</span>`) : item.text
            },
        }
    }

    get assets_options(): Select2Options {
        return {
            placeholder: 'Select Engine',
            minimumResultsForSearch: 3,
            ajax: {
                method: 'get',
                url: this.assetEndpoint,
            },
            templateResult: ({ id, title, text }: any) =>
                id ? $(`<span>${title}</span>`) : text,

            templateSelection: (item: any) => {
                if (item.id) this.engineCache[item.id] = item
                return item.id ? $(`<span>${item.title}</span>`) : item.text
            },
        }
    }

    get selectedEngines() {
        return this.asset.map(id => this.engineCache[id]).filter(Boolean)
    }

    removeEngine(id: string) {
        this.asset = this.asset.filter(a => a !== id)
    }

    showToast(message: string, type: 'success' | 'error') {
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: type,
            title: message,
            showConfirmButton: false,
            timer: 5000,
        })
    }

    get labels(): ButtonLabel {
        return {
            normal: 'Assign',
            loading: 'Assigning...',
            done: 'Assigned',
        }
    }

    @Watch('type')
    onTypeChange(newType: string) {
        this.location = ''
        this.asset = []
        this.assetEndpoint = ''

        if (newType === 'Warehouse') {
            this.endpoint = this.route('warehouse.ajax.select.warehouse')
        } else if (newType === 'Station') {
            this.endpoint = this.route('station.ajax.select.station')
        }
    }

    @Watch('location')
    onLocationChange(newLocation: string) {
        if (!newLocation) {
            this.assetEndpoint = ''
            return
        }

        this.asset = []

        if (this.type === 'Warehouse') {
            this.assetEndpoint = this.route('warehouse.ajax.select.engines', { warehouse: newLocation })
        } else if (this.type === 'Station') {
            this.assetEndpoint = this.route('station.ajax.select.engines', { station: newLocation })
        }
    }

    get checkInput() {
        return this.asset.length === 0
    }

    goBack() {
        window.history.back()
    }

    async onSubmit(payload: any) {
        this.loading = true

        payload.financier = this.financier.slug

        this.$http.post(this.route('financier.ajax.assign'), payload)
            .then((res: any) => {
                this.done = true
                this.showToast(res.data.message, 'success')
            })
            .catch((err: any) => {
                this.showToast(err.response?.data?.message, 'error')
            })
            .finally(() => this.loading = false)
    }
}

export default toNative(Create)
</script>

<style scoped>
.back-link {
    font-size: 0.8rem;
    color: #9ca3af;
    cursor: pointer;
}

.back-link:hover {
    color: #374151;
}

.page-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
}

/* TAB */
.tab-toggle {
    display: flex;
    background: #f3f4f6;
    padding: 4px;
    border-radius: 10px;
}

.toggle-btn {
    padding: 0.4rem 1rem;
    border: none;
    background: transparent;
}

/* PANEL */
.panel-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 2rem;
}

.panel-section-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #9ca3af;
}

/* LABELS */
.field-label {
    font-size: 0.8rem;
    font-weight: 600;
}

/* DIVIDER */
.divider {
    height: 1px;
    background: #f3f4f6;
}

.import-hint {
    font-size: 0.8rem;
    color: #6b7280;
}
</style>
